
import { useState, useMemo } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LibraryFilters } from "@/components/library/LibraryFilters";
import { LibraryControls } from "@/components/library/LibraryControls";
import { CreatorsView } from "@/components/library/CreatorsView";
import { RecipesView } from "@/components/library/RecipesView";
import { LibraryPagination } from "@/components/library/LibraryPagination";
import { LibraryResultsCount } from "@/components/library/LibraryResultsCount";
import { LibraryLoadingSkeleton } from "@/components/library/LibraryLoadingSkeleton";
import { useLibraryData } from "@/hooks/useLibraryData";
import { useLibraryCount } from "@/hooks/useLibraryCount";
import type { Recipe, Creator } from "@/hooks/useLibraryData";
import type { CuisineType, FoodType } from "@/types/recipe";

type SortOption = "newest" | "oldest" | "most_viewed" | "title_asc" | "title_desc";

interface LibraryProps {
  defaultFoodType?: FoodType;
}

const Library = ({ defaultFoodType }: LibraryProps = {}) => {
  const [search, setSearch] = useState("");
  const [selectedCreatorType, setSelectedCreatorType] = useState<string | null>(null);
  const [selectedNationality, setSelectedNationality] = useState<string | null>(null);
  const [selectedCuisineType, setSelectedCuisineType] = useState<CuisineType | null>(null);
  const [selectedFoodType, setSelectedFoodType] = useState<FoodType | null>(defaultFoodType || null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [viewMode, setViewMode] = useState<"recipes" | "creators">("recipes");
  const recipesPerPage = 40;

  const { data, isLoading } = useLibraryData({
    search,
    selectedCreatorType,
    selectedNationality,
    selectedCuisineType,
    selectedFoodType,
    currentPage,
    sortBy,
    viewMode,
    recipesPerPage,
  });

  const { totalCount, isLoading: isCountLoading } = useLibraryCount({
    search,
    selectedCreatorType,
    selectedNationality,
    selectedCuisineType,
    selectedFoodType,
    viewMode,
  });

  // Calculate available filters based on the current data
  const availableFilters = useMemo(() => {
    if (!data) return {
      creatorTypes: [],
      nationalities: [],
      cuisineTypes: [],
      foodTypes: []
    };

    if (viewMode === "creators") {
      const creators = data as Creator[];
      return {
        creatorTypes: Array.from(new Set(creators.flatMap(c => c.creator_type || []))),
        nationalities: Array.from(new Set(creators.map(c => c.nationality).filter(Boolean) as string[])),
        cuisineTypes: [],
        foodTypes: []
      };
    } else {
      const recipes = data as Recipe[];
      return {
        creatorTypes: Array.from(new Set(recipes.map(r => r.user?.creator_type || []).flat())),
        nationalities: Array.from(new Set(recipes.map(r => r.user?.nationality).filter(Boolean) as string[])),
        cuisineTypes: Array.from(new Set(recipes.map(r => r.cuisine_type).filter(Boolean) as CuisineType[])),
        foodTypes: Array.from(new Set(recipes.map(r => r.food_type).filter(Boolean) as FoodType[]))
      };
    }
  }, [data, viewMode]);

  const totalPages = Math.ceil((totalCount || 0) / recipesPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container max-w-7xl mx-auto py-8 px-4">
        <div className="flex flex-col md:flex-row gap-8">
          <LibraryFilters
            search={search}
            setSearch={setSearch}
            selectedCreatorType={selectedCreatorType}
            setSelectedCreatorType={setSelectedCreatorType}
            selectedNationality={selectedNationality}
            setSelectedNationality={setSelectedNationality}
            selectedCuisineType={selectedCuisineType}
            setSelectedCuisineType={setSelectedCuisineType}
            selectedFoodType={selectedFoodType}
            setSelectedFoodType={setSelectedFoodType}
            viewMode={viewMode}
            availableFilters={availableFilters}
          />

          <div className="flex-1 space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <LibraryResultsCount
                isLoading={isLoading}
                isCountLoading={isCountLoading}
                totalCount={totalCount || 0}
                viewMode={viewMode}
              />
              
              <LibraryControls
                viewMode={viewMode}
                setViewMode={setViewMode}
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </div>

            {isLoading ? (
              <LibraryLoadingSkeleton />
            ) : viewMode === "recipes" ? (
              <RecipesView recipes={data as Recipe[]} />
            ) : (
              <CreatorsView creators={data as Creator[]} />
            )}

            <LibraryPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Library;
